/**
 * Growth Mentor — full plan generator embedded inside Company Trajectory.
 *
 * The user picks a target revenue + horizon, the backend computes a
 * deterministic gap analysis (CAGR, monthly revenue at end-state, hires
 * needed, capacity gap) and the AI mentor produces:
 *   • 5 strategic pillars (Market Expansion, GTM, Product, Retention, Capital)
 *   • 90-day roadmap (W1-2, W3-4, M2, M3)
 *   • 3-4 market trend signals (tailwind / headwind)
 *   • 5 risks + mitigations
 *   • 5-7 GTM channels with expected ROAS bands
 *
 * Works irrespective of Tally — when no real numbers exist we use
 * company_parameters (sector, employees, etc.) and general market knowledge.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Sparkles, Target, Rocket, Map, Compass, AlertTriangle, Megaphone,
  TrendingUp, TrendingDown, Loader2, Wand2, CheckCircle2, ShieldCheck,
} from "lucide-react";
import { BACKEND_URL } from "../lib/backendUrl";
import HorizonInput from "./HorizonInput";

const API = `${BACKEND_URL}/api`;

// Real backend stages (growth_router.py's _run_growth_plan_job) mapped to
// user-facing copy. Kimi K2.6 takes ~55-90s for this call — see Fix Pack 1A
// (2026-07-29 Strategy Engine Audit) for why this now runs as a polled
// background job instead of one long-held HTTP request.
const STAGE_LABELS = {
  queued:               "Generating company strategy…",
  analysing_company:    "Analysing company…",
  analysing_financials: "Analysing financial position…",
  researching_market:   "Researching live market signals…",
  building_strategy:    "Building strategy…",
  finalising_plan:      "Finalising plan…",
};
const POLL_INTERVAL_MS = 2500;
// Fix Pack 1B (2026-07-29): richer context + output schema pushed real
// backend latency to ~180-220s (LLM_JOB_TIMEOUT_SECS in growth_router.py).
// Fix Pack 3 (2026-07-30): added live external research BEFORE the
// strategy call — up to ~60-90s more (4 concurrent Kimi $web_search
// round-trips). Give the poll loop headroom above the new worst case so we
// never show a false timeout error while the backend is still legitimately
// working.
const POLL_TIMEOUT_MS = 360_000;

// P0 ARCHITECTURE REDESIGN (2026-08): removed the fixed 1/3/5/10yr
// HORIZONS pill-selector — horizon is now a free positive number via the
// shared `HorizonInput` (years/months toggle), matching the canonical
// `strategic_targets.horizon_years` (float) and Company Trajectory's new
// Target Editor. No horizon is ever restricted to a preset set.

const FOCUS_OPTIONS = [
  "Default — balanced",
  "Market Expansion",
  "GTM Channels",
  "Product Positioning",
  "Customer Retention",
  "Capacity & Capital",
];

const DIFFICULTY_STYLES = {
  easy:      { label: "Easy",      cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  balanced:  { label: "Balanced",  cls: "bg-blue-50 text-blue-700 ring-blue-200"        },
  stretch:   { label: "Stretch",   cls: "bg-amber-50 text-amber-700 ring-amber-200"     },
  moonshot:  { label: "Moonshot",  cls: "bg-rose-50 text-rose-700 ring-rose-200"        },
  unknown:   { label: "Unknown",   cls: "bg-slate-50 text-slate-600 ring-slate-200"     },
};

const PILLAR_ICONS = {
  "Market Expansion":    Compass,
  "GTM Channels":        Megaphone,
  "Product Positioning": Sparkles,
  "Customer Retention":  Target,
  "Capacity & Capital":  Rocket,
};

function Chip({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ${className}`}>
      {children}
    </span>
  );
}

function StatTile({ label, value, hint, accent = "slate" }) {
  const accents = {
    slate:    "bg-slate-50 text-slate-700",
    blue:     "bg-blue-50 text-blue-700",
    emerald:  "bg-emerald-50 text-emerald-700",
    amber:    "bg-amber-50 text-amber-700",
    rose:     "bg-rose-50 text-rose-700",
  };
  return (
    <div className={`rounded-lg p-3 ${accents[accent] || accents.slate}`}>
      <div className="text-[10.5px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 text-[17px] font-bold tabular-nums">{value ?? "—"}</div>
      {hint && <div className="mt-0.5 text-[11px] opacity-70">{hint}</div>}
    </div>
  );
}

export default function GrowthMentorSection({ customerId, defaultTargetCr, defaultHorizon = 5, onTargetChanged, externalRefreshKey }) {
  const [targetCr, setTargetCr] = useState(defaultTargetCr || 18.2);
  const [horizon,  setHorizon]  = useState(defaultHorizon);
  const [focus,    setFocus]    = useState(FOCUS_OPTIONS[0]);
  const [plan,     setPlan]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [stage,    setStage]    = useState(null);
  const [error,    setError]    = useState(null);
  const pollRef = useRef(null);
  const jobIdRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // Fix Pack 1C (2026-07-30) — canonical strategic target. Prefill the form
  // from the ONE persisted target (survives refresh/logout) instead of only
  // the derived defaultTargetCr prop, which resets on every page load.
  const [canonicalTarget, setCanonicalTarget] = useState(null); // {target_value, horizon_years, focus, baseline_value, baseline_period}
  const hasCanonicalTargetRef = useRef(false);

  // fetchBadgeOnly: refreshes ONLY the `canonicalTarget` badge/state, never
  // touches the live form inputs (targetCr/horizon/focus). Safe to call at
  // any time (e.g. right after a plan finishes generating) without
  // clobbering whatever the founder currently has typed into the form.
  const fetchBadgeOnly = useCallback(async () => {
    if (!customerId) return;
    try {
      const r = await fetch(`${API}/intelligence/strategic-target`, { credentials: "include" });
      const j = await r.json();
      if (j?.target) {
        hasCanonicalTargetRef.current = true;
        setCanonicalTarget(j.target);
        onTargetChanged?.(j.target);
      }
    } catch (_) { /* no-op */ }
  }, [customerId, onTargetChanged]);

  // fetchCanonicalTarget: full prefill — badge state AND the live form
  // inputs. Only safe to call when there is no in-progress, unsaved form
  // input to protect: on initial mount, and right after a successful
  // Deploy (at which point the form's values ARE the just-deployed ones,
  // so this is a no-op re-sync, not a clobber).
  const fetchCanonicalTarget = useCallback(async () => {
    if (!customerId) return;
    try {
      const r = await fetch(`${API}/intelligence/strategic-target`, { credentials: "include" });
      const j = await r.json();
      if (j?.target) {
        hasCanonicalTargetRef.current = true;
        setCanonicalTarget(j.target);
        setTargetCr(j.target.target_value);
        setHorizon(Number(j.target.horizon_years));
        setFocus(j.target.focus && FOCUS_OPTIONS.includes(j.target.focus) ? j.target.focus : FOCUS_OPTIONS[0]);
        onTargetChanged?.(j.target);
      }
    } catch (_) { /* no-op — falls back to defaultTargetCr */ }
  }, [customerId, onTargetChanged]);

  // Direction Layer — deploy the trajectory as the active C-Suite strategy.
  const [active,     setActive]     = useState(null);   // {version, deployed_at, ...}
  const [confirming, setConfirming] = useState(false);
  const [deploying,  setDeploying]  = useState(false);
  const [deployNote, setDeployNote] = useState(null);

  const fetchActive = useCallback(async () => {
    if (!customerId) return;
    try {
      const r = await fetch(`${API}/intelligence/strategy/active`, { credentials: "include" });
      const j = await r.json();
      setActive(j?.active ? j.strategy : null);
      // Restore the previously DEPLOYED trajectory so it survives logout/login
      // and refresh — the founder never has to regenerate a deployed strategy.
      if (j?.plan && j.plan._restored) {
        setPlan((prev) => prev || j.plan);
        if (j.plan._target_revenue_cr != null) setTargetCr((v) => v ?? j.plan._target_revenue_cr);
        if (j.plan._horizon_years != null) setHorizon((h) => h || Number(j.plan._horizon_years));
        // Fix Pack 1C: the canonical strategic target (fetched first, below)
        // is the single source of truth for focus — only fall back to the
        // deployed plan's focus when NO canonical target exists at all, so
        // this restore can never silently override it (a real bug found
        // during Fix Pack 1C testing: both effects wrote `focus` with no
        // ordering, so a canonical focus outside FOCUS_OPTIONS could be
        // clobbered by this restore even though target/horizon stayed correct).
        if (j.plan._focus && !hasCanonicalTargetRef.current) {
          setFocus((f) => (f && f !== FOCUS_OPTIONS[0] ? f : j.plan._focus));
        }
      }
    } catch (_) { /* no-op */ }
  }, [customerId]);

  // Sequenced (not parallel useEffects): fetchCanonicalTarget MUST resolve
  // and set hasCanonicalTargetRef before fetchActive's restore logic runs,
  // otherwise the two focus writers race (see fix note above).
  useEffect(() => { fetchCanonicalTarget().then(fetchActive); }, [fetchCanonicalTarget, fetchActive]);

  // P0 ARCHITECTURE REDESIGN (2026-08): Company Trajectory's own Target
  // Editor can now set/edit the canonical target directly, without going
  // through this component's Generate/Deploy flow at all. When that
  // happens, the parent bumps `externalRefreshKey` so THIS badge re-syncs
  // immediately too — badge-only (never the live form inputs), same
  // safety rationale as `fetchBadgeOnly` above.
  const prevRefreshKeyRef = useRef(externalRefreshKey);
  useEffect(() => {
    if (externalRefreshKey !== undefined && externalRefreshKey !== prevRefreshKeyRef.current) {
      prevRefreshKeyRef.current = externalRefreshKey;
      fetchBadgeOnly();
    }
  }, [externalRefreshKey, fetchBadgeOnly]);

  const deploy = async () => {
    if (!customerId || !plan) return;
    setDeploying(true);
    setDeployNote(null);
    try {
      const body = {
        target_revenue_cr: Number(targetCr),
        horizon_years:     Number(horizon),
        focus:             focus && focus !== FOCUS_OPTIONS[0] ? focus : null,
        plan: {
          pillars:        plan.pillars || [],
          roadmap_90d:    plan.roadmap_90d || [],
          market_trends:  plan.market_trends || [],
          market_signals: plan.market_signals || [],
          risks:          plan.risks || [],
          channels:       plan.channels || [],
        },
        targets:    { gap: plan.gap || {}, target_revenue_cr: Number(targetCr) },
        milestones: plan.roadmap_90d || [],
        source_trajectory: { target_cr: Number(targetCr), horizon_years: Number(horizon), focus },
        research_snapshot_id: plan.research_snapshot_id || null,
      };
      const r = await fetch(`${API}/intelligence/strategy/deploy`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (j?.deployed) {
        setDeployNote({ ok: true, text: `Strategy v${j.version} is now active for all four executives.` });
        // P0 CONSISTENCY FIX (2026-08-08): deploy is now the ONLY action
        // that changes the canonical target (moved here from generate) —
        // refresh THIS component's own badge immediately too, not just the
        // parent's, so both update in the same tick with no page reload.
        await fetchCanonicalTarget();
        await fetchActive();
        onTargetChanged?.(canonicalTarget);
      } else {
        setDeployNote({ ok: false, text: j?.message || "Could not deploy strategy." });
      }
    } catch (e) {
      setDeployNote({ ok: false, text: e.message || "Could not deploy strategy." });
    } finally {
      setDeploying(false);
      setConfirming(false);
    }
  };

  const generate = async () => {
    setLoading(true);
    setError(null);
    setStage("queued");
    if (pollRef.current) clearTimeout(pollRef.current);

    try {
      const body = {
        target_revenue_cr: Number(targetCr),
        horizon_years:     Number(horizon),
        ...(focus && focus !== FOCUS_OPTIONS[0] ? { focus } : {}),
      };
      const res = await fetch(`${API}/intelligence/growth-plan/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let detail = "";
        try { detail = (await res.json())?.detail || ""; } catch (_) { /* no-op */ }
        throw new Error(typeof detail === "string" ? detail : "Plan generation failed");
      }
      const { job_id } = await res.json();
      jobIdRef.current = job_id;
      pollJob(job_id, Date.now());
    } catch (e) {
      setError(e.message || "Strategy generation could not be completed. Retry.");
      setLoading(false);
      setStage(null);
    }
  };

  // Polls the job status endpoint until it lands on completed/failed. A
  // failed job NEVER surfaces pillars/roadmap/etc — see growth_router.py's
  // _job_public(), which strips them unless generation_source === "llm".
  const pollJob = useCallback((jobId, startedAt) => {
    if (jobIdRef.current !== jobId) return; // a newer generate() superseded this poll
    (async () => {
      try {
        const res = await fetch(`${API}/intelligence/growth-plan/status/${jobId}`);
        if (!res.ok) throw new Error("status_check_failed");
        const j = await res.json();
        if (jobIdRef.current !== jobId) return; // stale response, a retry started meanwhile

        if (j.status === "completed" && j.generation_source === "llm") {
          setPlan(j);
          setStage(null);
          setLoading(false);
          // P0 CONSISTENCY FIX (2026-08-08): generating a plan no longer
          // upserts the canonical target (that only happens on Deploy).
          // BUG FIX (found by testing_agent_v4, iteration_91): this MUST
          // be the badge-only refresh, never the full one — the full
          // fetchCanonicalTarget() unconditionally overwrites the live
          // targetCr/horizon form inputs with the (unrelated, unchanged)
          // OLD canonical value, silently discarding whatever the founder
          // just typed and generated this exact plan against. If they
          // then clicked Deploy without re-typing, deploy() would read the
          // clobbered stale value instead of what plan.gap was actually
          // computed for — reintroducing the very self-contradiction (top-
          // level target vs plan.gap target disagreeing) this fix removed
          // from the generate() endpoint in the first place.
          fetchBadgeOnly();
          return;
        }
        if (j.status === "failed") {
          setError(j.failure_reason || "Strategy generation could not be completed. Retry.");
          setStage(null);
          setLoading(false);
          return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setError("Strategy generation is taking longer than expected. Retry.");
          setStage(null);
          setLoading(false);
          return;
        }
        setStage(j.stage || "queued");
        pollRef.current = setTimeout(() => pollJob(jobId, startedAt), POLL_INTERVAL_MS);
      } catch (_e) {
        if (jobIdRef.current !== jobId) return;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setError("Strategy generation could not be completed. Retry.");
          setStage(null);
          setLoading(false);
        } else {
          pollRef.current = setTimeout(() => pollJob(jobId, startedAt), POLL_INTERVAL_MS);
        }
      }
    })();
  }, [fetchBadgeOnly]);

  const diff = plan?.gap?.difficulty
    ? (DIFFICULTY_STYLES[plan.gap.difficulty] || DIFFICULTY_STYLES.unknown)
    : null;

  return (
    <section
      data-testid="growth-mentor"
      className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 shadow-card"
    >
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700">
            <Wand2 size={13} /> C-ORE Trajectory
          </div>
          <h2 className="mt-1 text-[19px] font-bold text-[#0F1B2D]">
            How do I reach my revenue target?
          </h2>
          <p className="mt-0.5 text-[12.5px] text-slate-500">
            Pick a target — we'll generate a sector-aware plan with channels, roadmap, risks and market signals.
          </p>
        </div>
        {canonicalTarget && (
          <div data-testid="growth-canonical-target-badge"
            className="flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-[11.5px] font-medium text-indigo-700">
            <Target size={12} />
            Strategic target · ₹{canonicalTarget.target_value}Cr in {canonicalTarget.horizon_years}y
            {canonicalTarget.baseline_value != null && (
              <span className="text-indigo-500">(Last FY Actual ₹{canonicalTarget.baseline_value}Cr)</span>
            )}
          </div>
        )}
      </div>

      {/* Form */}
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-12">
        <div className="md:col-span-3">
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            Target revenue
          </label>
          <div className="mt-1 flex items-center rounded-md border border-slate-200 bg-white">
            <span className="px-2 text-[13px] text-slate-500">₹</span>
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={targetCr}
              onChange={(e) => setTargetCr(e.target.value)}
              data-testid="growth-target-input"
              className="w-full bg-transparent px-2 py-2 text-[14px] tabular-nums focus:outline-none"
            />
            <span className="pr-2 text-[12px] text-slate-500">Cr</span>
          </div>
        </div>

        <div className="md:col-span-3">
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            Horizon
          </label>
          <div className="mt-1">
            <HorizonInput horizonYears={horizon} onChange={setHorizon} testIdPrefix="growth-horizon" />
          </div>
        </div>

        <div className="md:col-span-4">
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            Focus area (optional)
          </label>
          <select
            data-testid="growth-focus-select"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-[13px] focus:border-emerald-500 focus:outline-none"
          >
            {FOCUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <div className="md:col-span-2 md:flex md:items-end">
          <button
            type="button"
            onClick={generate}
            disabled={loading || !targetCr}
            data-testid="growth-generate-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading
              ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
              : <><Sparkles size={14} /> Generate plan</>}
          </button>
        </div>
      </div>

      {/* Progressive stage feedback — real backend stages (analysing_company →
          analysing_financials → building_strategy → finalising_plan), not a
          fake timer. Kimi K2.6 takes ~55-90s for this call. */}
      {loading && stage && (
        <div
          data-testid="growth-generating-status"
          className="mt-4 flex items-center gap-2.5 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2.5 text-[12.5px] text-blue-800"
        >
          <Loader2 size={14} className="animate-spin shrink-0" />
          <span>{STAGE_LABELS[stage] || STAGE_LABELS.queued}</span>
        </div>
      )}

      {error && (
        <div
          data-testid="growth-error"
          className="mt-4 flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={generate}
            data-testid="growth-retry-btn"
            className="shrink-0 rounded-md border border-rose-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-rose-700 hover:bg-rose-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Deploy hint — shown before a plan exists so users know the path to
          making this the company's active strategy. */}
      {!plan && !loading && (
        <div
          data-testid="deploy-hint"
          className="mt-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2.5 text-[12.5px] text-emerald-800"
        >
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
          <span>
            <span className="font-semibold">Generate a plan first</span> — once it's ready you can
            {" "}<span className="font-semibold">Deploy Strategy</span> to make this the active target
            for your CEO, CFO, CMO &amp; CTO.
            {active ? <> Currently active: <span className="font-semibold">Strategy v{active.version}</span>.</> : null}
          </span>
        </div>
      )}

      {/* Plan output */}
      {plan && (
        <div className="mt-6 space-y-6" data-testid="growth-plan-output">
          {/* Source / context strip */}
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            {plan.context?.sector && <Chip className="bg-slate-100 text-slate-700 ring-slate-200">Sector · {plan.context.sector}</Chip>}
            {plan.context?.city && <Chip className="bg-slate-100 text-slate-700 ring-slate-200">{plan.context.city}</Chip>}
            {plan._restored
              ? <Chip className="bg-indigo-50 text-indigo-700 ring-indigo-200">Restored · Strategy v{plan._version}</Chip>
              : plan.source === "ai"
                ? <Chip className="bg-emerald-50 text-emerald-700 ring-emerald-200">C-ORE generated</Chip>
                : <Chip className="bg-amber-50 text-amber-700 ring-amber-200">Engine plan</Chip>}
            {plan.context?.demo && <Chip className="bg-blue-50 text-blue-700 ring-blue-200">Demo profile</Chip>}
          </div>

          {/* Direction Layer — Deploy Strategy */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="deploy-strategy-bar">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-700 shrink-0" />
                <div>
                  <div className="text-[13px] font-semibold text-[#0F1B2D]">Make this the company's active strategy</div>
                  <div className="text-[12px] text-slate-600">
                    {active
                      ? <>Currently active: <span className="font-semibold" data-testid="active-strategy-version">Strategy v{active.version}</span>
                          {active.deployed_at ? ` · deployed ${new Date(active.deployed_at).toLocaleDateString()}` : ""}. Deploying replaces it.</>
                      : "No strategy deployed yet — your executives work on today's data only."}
                  </div>
                </div>
              </div>
              {!confirming ? (
                <button
                  type="button"
                  onClick={() => { setConfirming(true); setDeployNote(null); }}
                  disabled={!customerId}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F1B2D] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#1b2f4d] disabled:opacity-50"
                  data-testid="deploy-strategy-btn">
                  <Rocket className="h-4 w-4" /> Deploy Strategy
                </button>
              ) : (
                <div className="flex flex-col items-stretch gap-2 rounded-lg border border-emerald-300 bg-white p-3" data-testid="deploy-confirm">
                  <p className="text-[12px] text-slate-700 max-w-xs">
                    This becomes the active target for your CEO, CFO, CMO &amp; CTO until you deploy again. Proceed?
                  </p>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={deploy} disabled={deploying}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                      data-testid="deploy-confirm-btn">
                      {deploying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Yes, deploy
                    </button>
                    <button type="button" onClick={() => setConfirming(false)} disabled={deploying}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50"
                      data-testid="deploy-cancel-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            {deployNote && (
              <div className={`mt-3 text-[12px] font-medium ${deployNote.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="deploy-note">
                {deployNote.text}
              </div>
            )}
          </div>

          {/* Gap analysis */}
          <div data-testid="growth-gap" className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Gap analysis · Last FY Actual → Strategic Target</div>
                <div className="mt-0.5 text-[15px] font-bold text-[#0F1B2D]">
                  {plan.gap?.current_display} <span className="text-slate-400">→</span> {plan.gap?.target_display}
                  <span className="ml-2 text-[12px] font-medium text-slate-500">over {plan.gap?.horizon_display || `${plan.gap?.horizon_years}y`}</span>
                </div>
              </div>
              {diff && <Chip className={diff.cls}>{diff.label}</Chip>}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
              <StatTile
                label="Required CAGR"
                value={plan.gap?.required_cagr_pct != null ? `${plan.gap.required_cagr_pct}%` : "—"}
                hint="Year on year"
                accent="blue"
              />
              <StatTile
                label="Revenue Gap"
                value={plan.gap?.gap_display}
                hint="Target − Last FY Actual"
                accent="emerald"
              />
              <StatTile
                label="Growth Multiple"
                value={plan.gap?.multiplier != null ? `${plan.gap.multiplier}×` : "—"}
                hint="Target ÷ Last FY Actual"
              />
              <StatTile
                label="Hires needed"
                value={plan.gap?.hires_needed != null ? plan.gap.hires_needed : "—"}
                hint={plan.gap?.rev_per_employee_cr ? `at ₹${(plan.gap.rev_per_employee_cr).toFixed(2)}Cr/emp` : "set employee count to estimate"}
                accent={plan.gap?.hires_needed > 50 ? "amber" : "slate"}
              />
              <StatTile
                label="Capacity gap"
                value={plan.gap?.capacity_gap_pct != null ? `${plan.gap.capacity_gap_pct}%` : "—"}
                hint="vs. current line"
                accent={plan.gap?.capacity_gap_pct > 30 ? "rose" : "slate"}
              />
            </div>
            {/* Yearly milestones */}
            {Array.isArray(plan.gap?.milestones) && plan.gap.milestones.length > 0 && (
              <div className="mt-4">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Year-on-year checkpoints</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {plan.gap.milestones.map((m) => (
                    <div key={m.year_index} data-testid={`growth-milestone-${m.year_index}`}
                      className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px]">
                      <span className="font-semibold text-slate-600">{m.label}</span>
                      <span className="ml-1 tabular-nums text-[#0F1B2D]">{m.display}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Strategic Pillars */}
          {plan.pillars?.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                <Compass size={12} /> 5 Strategic pillars
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {plan.pillars.map((raw, i) => {
                  // Defensive: a pillar should be {title, objective, actions[],
                  // kpis[], owner}, but tolerate a bare string (older/malformed
                  // rows) instead of silently rendering a blank card.
                  const p = typeof raw === "string" ? { title: raw } : (raw || {});
                  const Icon = PILLAR_ICONS[p.title] || Sparkles;
                  return (
                    <div key={i} data-testid={`growth-pillar-${i}`}
                      className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex items-center gap-2 text-[13px] font-bold text-[#0F1B2D]">
                        <Icon size={14} className="text-emerald-600" />
                        {p.title || "Untitled pillar"}
                      </div>
                      {p.owner && <div className="mt-0.5 text-[10.5px] uppercase tracking-wider text-slate-400">Owner · {p.owner}</div>}
                      <ul className="mt-2 space-y-1 text-[12.5px] text-slate-700">
                        {(p.actions || []).map((a, j) => (
                          <li key={j} className="flex gap-1.5"><span className="text-emerald-600">▶</span>{a}</li>
                        ))}
                      </ul>
                      {(p.kpis || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {p.kpis.map((k, j) => (
                            <Chip key={j} className="bg-blue-50 text-blue-700 ring-blue-200">{k}</Chip>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 90-day roadmap */}
          {plan.roadmap_90d?.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                <Map size={12} /> 90-day roadmap
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                {plan.roadmap_90d.map((r, i) => (
                  <div key={i} data-testid={`growth-roadmap-${i}`}
                    className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{r.when}</div>
                    <div className="mt-0.5 text-[13px] font-bold text-[#0F1B2D]">{r.phase}</div>
                    <ul className="mt-2 space-y-1 text-[12px] text-slate-700">
                      {(r.actions || []).map((a, j) => (
                        <li key={j} className="flex gap-1.5"><span className="text-slate-400">·</span>{a}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Market Opportunities & Signals (Fix Pack 3, 2026-07-30) —
              replaces the old generic "Market trend signals" section.
              Prefers the new `market_signals` shape (signal/why_it_matters/
              source/date/confidence/evidence_type); falls back to the
              legacy `market_trends` shape only when restoring an OLDER
              deployed strategy that predates this fix pack. */}
          {plan.market_signals?.length > 0 ? (
            <div data-testid="growth-market-signals">
              <div className="mb-2 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                <TrendingUp size={12} /> Market opportunities & signals
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {plan.market_signals.map((s, i) => {
                  const conf = (s.confidence || "medium").toLowerCase();
                  const confStyle = conf === "high" ? "bg-emerald-100 text-emerald-700"
                    : conf === "low" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-700";
                  const isExternal = (s.evidence_type || "").toLowerCase() === "external_signal";
                  return (
                    <div key={i} data-testid={`growth-market-signal-${i}`}
                      className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 text-[12.5px] font-bold text-[#0F1B2D]">
                          <TrendingUp size={13} className="text-indigo-600" />
                          {s.signal}
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${confStyle}`}>
                          {conf} confidence
                        </span>
                      </div>
                      <div className="mt-1 text-[12px] text-slate-700">{s.why_it_matters}</div>
                      <div className="mt-2 flex items-center gap-1.5 text-[10.5px] text-slate-500">
                        <span className="font-semibold">{isExternal ? "Unverified source" : "Basis"} ·</span>
                        <span className="truncate">{s.source || "C-ORE analysis"}</span>
                        {s.date && <span>· {s.date}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-2 text-[10.5px] text-slate-400">
                Signals tagged "Unverified source" come from a live Kimi web search — sources/dates are carried through as-is, not independently re-verified by C-ORE.
              </p>
            </div>
          ) : plan.market_trends?.length > 0 && (
            <div data-testid="growth-market-signals-legacy">
              <div className="mb-2 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                <TrendingUp size={12} /> Market opportunities & signals
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {plan.market_trends.map((t, i) => {
                  const isUp = (t.type || "").toLowerCase() === "tailwind";
                  return (
                    <div key={i} data-testid={`growth-trend-${i}`}
                      className={`rounded-lg border p-3 ${isUp ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}>
                      <div className="flex items-center gap-2 text-[12.5px] font-bold text-[#0F1B2D]">
                        {isUp
                          ? <TrendingUp size={13} className="text-emerald-600" />
                          : <TrendingDown size={13} className="text-amber-600" />}
                        {t.label}
                      </div>
                      <div className="mt-1 text-[12px] text-slate-700">{t.body}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Risks */}
          {plan.risks?.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                <AlertTriangle size={12} /> Top {plan.risks.length} risks & mitigations
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {plan.risks.map((r, i) => (
                  <div key={i} data-testid={`growth-risk-${i}`}
                    className="rounded-lg border border-rose-200 bg-rose-50/30 p-3">
                    <div className="text-[12.5px] font-bold text-[#0F1B2D]">{r.title}</div>
                    <div className="mt-1 text-[12px] text-slate-700">{r.body}</div>
                    {r.mitigation && (
                      <div className="mt-2 text-[11.5px] text-emerald-800">
                        <span className="font-semibold uppercase tracking-wider">Mitigation · </span>
                        {r.mitigation}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Channels */}
          {plan.channels?.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                <Megaphone size={12} /> Channel & GTM playbook
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Channel</th>
                      <th className="px-3 py-2 text-left">Fit</th>
                      <th className="px-3 py-2 text-left">Expected ROAS</th>
                      <th className="px-3 py-2 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.channels.map((c, i) => {
                      const fit = (c.fit || "").toLowerCase();
                      const fitCls = fit === "high"
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : fit === "low"
                          ? "bg-slate-100 text-slate-500 ring-slate-200"
                          : "bg-blue-50 text-blue-700 ring-blue-200";
                      return (
                        <tr key={i} data-testid={`growth-channel-${i}`} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-semibold text-[#0F1B2D]">{c.name}</td>
                          <td className="px-3 py-2"><Chip className={fitCls}>{c.fit || "—"}</Chip></td>
                          <td className="px-3 py-2 tabular-nums text-slate-700">{c.expected_roas || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">{c.notes || ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
